'use client'

import { useState } from 'react'
import { useLocale, useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  ArrowLeftIcon,
  EditIcon,
  Trash2Icon,
  PlayIcon,
  ServerIcon,
  CalendarIcon,
  ClockIcon,
  DatabaseIcon,
  HardDriveIcon,
  ArchiveIcon,
  DownloadIcon,
  CopyIcon
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { ContentError, DeleteConfirmationDialog, JobStatusBadge } from '@/components/partials'
import {
  useBackupSchedule,
  useDeleteBackupSchedule,
  useRunBackupSchedule,
  useDeleteBackupRun,
  useCopyToClipboard
} from '@/hooks'
import { DayOfWeekEnum, JobStatusEnum } from '@/enums'
import { formatDateTime, formatFileSize } from '@/lib/utils'
import { useMeStore, useFilesStore } from '@/store'
import { hasPermission } from '@/lib/permissions'

type PropsType = {
  id: string
}

const BackupScheduleDetailSkeleton = () => (
  <div className="space-y-6">
    {/* Header */}
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-4">
        <Skeleton className="h-9 w-16" />
        <div className="space-y-1">
          <Skeleton className="h-8 w-44" />
          <Skeleton className="h-4 w-64" />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Skeleton className="h-9 w-24" />
        <Skeleton className="h-9 w-16" />
        <Skeleton className="h-9 w-20" />
      </div>
    </div>

    {/* Schedule config card */}
    <Card>
      <CardHeader>
        <Skeleton className="h-6 w-40" />
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {/* Days – full row */}
          <div className="space-y-2 md:col-span-2">
            <Skeleton className="h-4 w-12" />
            <div className="flex flex-wrap gap-1">
              {Array.from({ length: 7 }).map((_, i) => (
                <Skeleton key={i} className="h-5 w-14 rounded-full" />
              ))}
            </div>
          </div>
          {/* Time */}
          <div className="space-y-2">
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-7 w-20" />
          </div>
          {/* Keep count */}
          <div className="space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-5 w-8" />
          </div>
          {/* Enabled */}
          <div className="space-y-2">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-5 w-16 rounded-full" />
          </div>
          {/* Include database */}
          <div className="space-y-2">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-5 w-10 rounded-full" />
          </div>
          {/* Include storage */}
          <div className="space-y-2">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-5 w-10 rounded-full" />
          </div>
          {/* Last run at */}
          <div className="space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-36" />
          </div>
          {/* Next run at */}
          <div className="space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-36" />
          </div>
        </div>

        <Separator className="my-6" />

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="space-y-2">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-36" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-36" />
          </div>
        </div>
      </CardContent>
    </Card>

    {/* Backup runs card */}
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Skeleton className="h-6 w-36" />
          <Skeleton className="h-5 w-8 rounded-full" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <div className="divide-y">
            {/* Table header */}
            <div className="grid grid-cols-6 gap-4 p-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-4 w-full" />
              ))}
            </div>
            {/* Table rows */}
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="grid grid-cols-6 gap-4 p-3">
                <Skeleton className="h-5 w-20 rounded-full" />
                <Skeleton className="mx-auto h-5 w-8 rounded-full" />
                <Skeleton className="mx-auto h-5 w-8 rounded-full" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-32" />
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
)

const BackupScheduleDetail = ({ id }: PropsType) => {
  const t = useTranslations()
  const locale = useLocale()
  const router = useRouter()
  const queryClient = useQueryClient()
  const { copyToClipboard } = useCopyToClipboard()
  const { meState } = useMeStore()
  const addToDownloadQueue = useFilesStore(state => state.addToDownloadQueue)
  const currentUser = meState.response
  const canUpdate = hasPermission(currentUser, 'BACKUPS', 'UPDATE')
  const canDelete = hasPermission(currentUser, 'BACKUPS', 'DELETE')

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [deleteRunId, setDeleteRunId] = useState<string | null>(null)

  const { data: backup, isLoading, error } = useBackupSchedule(id)
  const { mutate: deleteBackup, isPending: isDeleting } = useDeleteBackupSchedule()
  const { mutate: deleteBackupRun, isPending: isDeletingRun } = useDeleteBackupRun()
  const { mutate: runNow, isPending: isRunning } = useRunBackupSchedule()

  const handleRunNow = () => {
    runNow(id, {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: ['backup-schedules'] })
        await queryClient.invalidateQueries({ queryKey: ['backup-schedule', id] })
        toast.success(t('backup_run_success'))
      },
      onError: () => {
        toast.error(t('error_updating'))
      }
    })
  }

  const confirmDelete = () => {
    deleteBackup(id, {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: ['backup-schedules'] })
        await queryClient.invalidateQueries({ queryKey: ['backup-schedule', id] })
        toast.success(t('deleted_successfully'))
        router.push('/backups/schedules')
      },
      onError: () => {
        toast.error(t('error_deleting'))
        setDeleteConfirmOpen(false)
      }
    })
  }

  const confirmDeleteRun = () => {
    if (!deleteRunId) {
      return
    }
    const runId = deleteRunId
    deleteBackupRun(runId, {
      onSuccess: async () => {
        // Immediately remove from schedule detail cache
        const scheduleDetailQueries = queryClient.getQueriesData({ queryKey: ['backup-schedule', id] })
        scheduleDetailQueries.forEach(([queryKey, oldData]: [any, any]) => {
          if (!oldData || !oldData.backups) {
            return
          }
          queryClient.setQueryData(queryKey, {
            ...oldData,
            backupsCount: Math.max(0, (oldData.backupsCount ?? 1) - 1),
            backups: oldData.backups.filter((item: any) => item.id !== runId)
          })
        })
        // Also remove from backup list cache
        const listQueries = queryClient.getQueriesData({ queryKey: ['backups'] })
        listQueries.forEach(([queryKey, oldData]: [any, any]) => {
          if (!oldData || !oldData.items) {
            return
          }
          if (oldData.items.some((item: any) => item.id === runId)) {
            queryClient.setQueryData(queryKey, {
              ...oldData,
              total: Math.max(0, (oldData.total ?? 1) - 1),
              items: oldData.items.filter((item: any) => item.id !== runId)
            })
          }
        })
        toast.success(t('deleted_successfully'))
        setDeleteRunId(null)
      },
      onError: () => {
        toast.error(t('error_deleting'))
        setDeleteRunId(null)
      }
    })
  }

  if (isLoading) {
    return <BackupScheduleDetailSkeleton />
  }

  if (error || !backup) {
    return <ContentError error={error} listUrl="/backup-schedules" />
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="grid grid-cols-1 items-center justify-between gap-2 md:grid-cols-2">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={() => router.push('/backups/schedules')}>
              <ArrowLeftIcon className="h-4 w-4" />
              <span className="hidden lg:inline">{t('back')}</span>
            </Button>
            <div>
              <h1 className="text-md flex items-center gap-2 font-bold lg:text-2xl">
                <ServerIcon className="h-6 w-6" />
                {t('backup_schedule')}
              </h1>
              <div className="flex items-center gap-2">
                <p className="text-muted-foreground text-sm">{backup.id}</p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(backup.id, t('copied'))}
                  className="h-6 w-6 p-0">
                  <CopyIcon className="h-3 w-3" />
                </Button>
              </div>
            </div>
          </div>
          <div className="flex flex-row items-center justify-end gap-2">
            <Button variant="outline" onClick={handleRunNow} disabled={isRunning}>
              <PlayIcon className="h-4 w-4" />
              <span className="hidden lg:inline">{t('run_now')}</span>
            </Button>
            {canUpdate && (
              <Button variant="outline" onClick={() => router.push(`/backups/schedules/${id}/edit`)}>
                <EditIcon className="h-4 w-4" />
                <span className="hidden lg:inline">{t('edit_backup_schedule')}</span>
              </Button>
            )}
            {canDelete && (
              <Button variant="destructive" onClick={() => setDeleteConfirmOpen(true)}>
                <Trash2Icon className="h-4 w-4" />
                <span className="hidden lg:inline">{t('delete')}</span>
              </Button>
            )}
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ServerIcon className="h-5 w-5" />
              {t('schedule_config')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <p className="text-muted-foreground flex items-center gap-1 text-sm font-medium">
                  <CalendarIcon className="h-4 w-4" />
                  {t('days')}
                </p>
                <div className="flex flex-wrap gap-1">
                  {Object.values(DayOfWeekEnum).map(day => (
                    <Badge
                      key={day}
                      variant={(backup.days ?? []).includes(day) ? 'default' : 'outline'}
                      className="text-xs">
                      {t(`day_${day}` as any)}
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-muted-foreground flex items-center gap-1 text-sm font-medium">
                  <ClockIcon className="h-4 w-4" />
                  {t('time')}
                </p>
                <p className="font-mono text-lg font-semibold">
                  {String(backup.hour).padStart(2, '0')}:{String(backup.minute).padStart(2, '0')}
                </p>
              </div>

              <div className="space-y-2">
                <p className="text-muted-foreground text-sm font-medium">{t('keep_count')}</p>
                <p className="font-semibold">{backup.keepCount}</p>
              </div>

              <div className="space-y-2">
                <p className="text-muted-foreground text-sm font-medium">{t('enabled')}</p>
                <Badge variant={backup.enabled ? 'success' : 'secondary'}>
                  {backup.enabled ? t('enabled') : t('disabled')}
                </Badge>
              </div>

              <div className="space-y-2">
                <p className="text-muted-foreground flex items-center gap-1 text-sm font-medium">
                  <DatabaseIcon className="h-4 w-4" />
                  {t('include_database')}
                </p>
                <Badge variant={backup.includeDatabase ? 'success' : 'outline'}>
                  {backup.includeDatabase ? t('yes') : t('no')}
                </Badge>
              </div>

              <div className="space-y-2">
                <p className="text-muted-foreground flex items-center gap-1 text-sm font-medium">
                  <HardDriveIcon className="h-4 w-4" />
                  {t('include_storage')}
                </p>
                <Badge variant={backup.includeStorage ? 'success' : 'outline'}>
                  {backup.includeStorage ? t('yes') : t('no')}
                </Badge>
              </div>

              <div className="space-y-2">
                <p className="text-muted-foreground flex items-center gap-1 text-sm font-medium">
                  <CalendarIcon className="h-4 w-4" />
                  {t('last_run_at')}
                </p>
                <p className="text-sm">{backup.lastRunAt ? formatDateTime(backup.lastRunAt, locale) : '-'}</p>
              </div>

              <div className="space-y-2">
                <p className="text-muted-foreground flex items-center gap-1 text-sm font-medium">
                  <CalendarIcon className="h-4 w-4" />
                  {t('next_run_at')}
                </p>
                <p className="text-sm">{backup.nextRunAt ? formatDateTime(backup.nextRunAt, locale) : '-'}</p>
              </div>
            </div>

            <Separator className="my-6" />

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <p className="text-muted-foreground text-sm font-medium">{t('created_at')}</p>
                <p className="text-sm">{formatDateTime(backup.createdAt, locale)}</p>
              </div>
              <div className="space-y-2">
                <p className="text-muted-foreground text-sm font-medium">{t('updated_at')}</p>
                <p className="text-sm">{formatDateTime(backup.updatedAt, locale)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ArchiveIcon className="h-5 w-5" />
              {t('backup_records')}
              <Badge variant="outline" className="ml-2">
                {backup.backupsCount}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {backup.backups?.length === 0 ? (
              <p className="text-muted-foreground py-8 text-center">{t('no_results')}</p>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('status')}</TableHead>
                      <TableHead className="text-center">
                        <DatabaseIcon className="mr-1 inline h-4 w-4" />
                        {t('database')}
                      </TableHead>
                      <TableHead className="text-center">
                        <HardDriveIcon className="mr-1 inline h-4 w-4" />
                        {t('storage')}
                      </TableHead>
                      <TableHead>{t('message')}</TableHead>
                      <TableHead>{t('file')}</TableHead>
                      <TableHead className="text-right">{t('size')}</TableHead>
                      <TableHead>
                        <CalendarIcon className="mr-1 inline h-4 w-4" />
                        {t('created_at')}
                      </TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {backup.backups?.map((record, index) => (
                      <TableRow
                        key={record.id}
                        className={`cursor-pointer ${index % 2 === 0 ? 'bg-background' : 'bg-muted/25'}`}
                        onClick={() => router.push(`/backups/${record.id}`)}>
                        <TableCell>
                          <JobStatusBadge status={record.status as JobStatusEnum} />
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant={record.includeDatabase ? 'success' : 'outline'} className="text-xs">
                            {record.includeDatabase ? t('yes') : t('no')}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant={record.includeStorage ? 'success' : 'outline'} className="text-xs">
                            {record.includeStorage ? t('yes') : t('no')}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground max-w-48 truncate text-sm">
                          {record.message
                            ? t.has(record.message as any)
                              ? t(record.message as any)
                              : record.message
                            : '-'}
                        </TableCell>
                        <TableCell className="text-muted-foreground max-w-32 truncate text-sm">
                          {record.file ? record.file.name : '-'}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-right text-sm tabular-nums">
                          {record.file ? formatFileSize(record.file.size) : '-'}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {formatDateTime(record.createdAt, locale)}
                        </TableCell>
                        <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1">
                            {record.file && (
                              <Button size="icon" className="h-8 w-8" onClick={() => addToDownloadQueue(record.file!)}>
                                <DownloadIcon className="h-4 w-4" />
                              </Button>
                            )}
                            {canDelete && (
                              <Button
                                variant="destructive"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => setDeleteRunId(record.id)}>
                                <Trash2Icon className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <DeleteConfirmationDialog
        open={deleteConfirmOpen}
        onCancel={() => setDeleteConfirmOpen(false)}
        onConfirm={confirmDelete}
        isDeleting={isDeleting}
      />
      <DeleteConfirmationDialog
        open={!!deleteRunId}
        onCancel={() => setDeleteRunId(null)}
        onConfirm={confirmDeleteRun}
        isDeleting={isDeletingRun}
      />
    </>
  )
}

export default BackupScheduleDetail
